// @ts-strict-ignore
import React, { useContext } from 'react';
import type { Expression } from 'flow/store/engine/types';

import type { MLAAttributeItemOrigin } from '../../NodeEditorDrawer/types';
import { RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import { useAppSelector } from '~/store/reduxHooks';
import {
  selectPendingGroup,
  selectWorkflowData,
  selectWorkflowProps,
} from '~/dashboard/views/workflows/store/redux/selectors';
import MLAAttributeContainer from '../../mla/MLAAttributeContainer/MLAAttributeContainer';
import { AggregationDataRouteWrapper } from '../../aggregations';
import ExpressionBuilderDropdown from '../ExpressionBuilderDropdown';
import { StyledLineBreakDivider } from './styles';
import MorphingCaret from '../MorphingCaret';
import { getExpressionPathInDotNotationFormat } from './helpers';
import { isMLAAttributeItemOrigin } from '../../NodeEditorDrawer/ThresholdsTab/helpers';
import { isAggregationServiceAction } from '~/dashboard/views/workflows/service/services';

interface RuleLineItemProps {
  lineIndex: number;
  expressions: Expression;
}

function ExpressionLineItem({
  lineIndex,
  expressions,
}: RuleLineItemProps) {
  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const { services } = useAppSelector(selectWorkflowProps);
  const pendingGroup = useAppSelector(selectPendingGroup);

  const {
    semanticLineBreakData,
    isolatedEditingBlock,
    pendingGroupMaxRange: {
      allPossibleGroupClosingPaths,
    },
  } = useContext(RuleEditorContext);

  const isolatedEditorEnabledForThisLine = (
    semanticLineBreakData?.groupedByLines[lineIndex]?.mlaEditorInThisLine
      && isMLAAttributeItemOrigin(isolatedEditingBlock.originType)
  ) ? isolatedEditingBlock.originType
    : false;

  const editedDataRoute = isolatedEditorEnabledForThisLine
    ? dataRoutes[isolatedEditingBlock.originPublicId]
    : null;
  const isAggregationEditor = editedDataRoute
    && isAggregationServiceAction(actions[editedDataRoute.sourceId]);

  const renderLineItem = (item: string) => {
    const { data, path, type } = getExpressionPathInDotNotationFormat({
      expressions,
      itemFlatCaret: item,
      workflowInfo: { actions, dataRoutes, services },
    });
    const isDataRoute = type === RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE
      || type === RuleEditorDropdownMode.DATAROUTE_SOURCE;
    const canShowMorphingCaret = (
      type !== RuleEditorDropdownMode.DATAROUTE_SOURCE
      || (type === RuleEditorDropdownMode.DATAROUTE_SOURCE && !data?.attribute?.id)
    );
    const canCloseGroup = pendingGroup.path !== null
      && allPossibleGroupClosingPaths?.includes(path)
      && type !== RuleEditorDropdownMode.GROUP_START;

    return (
      <>
        <ExpressionBuilderDropdown
          key={`${path}-${type}`}
          expression={isDataRoute ? undefined : data}
          dataRoute={isDataRoute ? data : undefined}
          expressions={expressions}
          expressionPath={path}
          mode={type}
        />
        {canShowMorphingCaret
          && (
          <MorphingCaret
            key={`${path}-${type}-${RuleEditorDropdownMode.CARET_EXPRESSION}`}
            position={item}
            canShowGroupCloseSuggestion={canCloseGroup}
          >
            <ExpressionBuilderDropdown
              expressionPath={path}
              expressions={expressions}
              mode={RuleEditorDropdownMode.CARET_EXPRESSION}
              canAddGroupCloseOption={canCloseGroup}
            />
          </MorphingCaret>
          )}
      </>
    );
  };

  const lineItems = semanticLineBreakData?.groupedByLines[lineIndex]?.items;

  if (!lineItems) return null;

  return (
    <>
      {lineItems.map(renderLineItem)}
      <StyledLineBreakDivider />
      {isolatedEditorEnabledForThisLine && (
      <>
        {isAggregationEditor ? (
          <AggregationDataRouteWrapper dataRoute={editedDataRoute} />
        ) : (
          <MLAAttributeContainer
            origin={isolatedEditorEnabledForThisLine as MLAAttributeItemOrigin}
            data={{ dataRoute: dataRoutes[isolatedEditingBlock.originPublicId] }}
          />
        )}
        <StyledLineBreakDivider />
      </>
      )}
    </>
  );
}

export default ExpressionLineItem;
